import { Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useState, useEffect } from 'react';
import { useAdvancedSearchTheme } from '@/shared/ui/search/AdvancedSearch/theme/useAdvancedSearchTheme';
import { SearchField, FieldOption } from '@/shared/ui/search/AdvancedSearch/types/field.types';
import { FilterValue } from '@/shared/ui/search/AdvancedSearch/types/filter.types';
import { CascadeValue } from '@/shared/ui/search/AdvancedSearch/types/cascade-field.types';

interface IProps {
  field: SearchField;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
}

export function CascadeSelect({ field, value, onChange }: IProps) {
  const { valueInputSx } = useAdvancedSearchTheme();
  const cascadeConfig = field.cascadeConfig;

  if (!cascadeConfig) {
    console.error('CascadeSelect requires cascadeConfig');
    return null;
  }

  const [selections, setSelections] = useState<(string | number | null)[]>(
    cascadeConfig.levels.map(() => null)
  );

  const [optionsPerLevel, setOptionsPerLevel] = useState<FieldOption[][]>(
    cascadeConfig.levels.map(() => [])
  );

  useEffect(() => {
    initializeSelectionsFromValue(value, [...cascadeConfig.levels], setSelections);
  }, [value, cascadeConfig.levels]);

  useEffect(() => {
    loadOptionsForLevel(0, undefined, cascadeConfig, setOptionsPerLevel);
  }, []);

  const handleLevelChange = async (level: number, selectedValue: string | number) => {
    handleLevelChangeImpl(
      level,
      selectedValue,
      selections,
      setSelections,
      cascadeConfig,
      optionsPerLevel,
      setOptionsPerLevel,
      onChange
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {cascadeConfig.levels.map((level, idx) => {
        const options = optionsPerLevel[idx] || [];
        const isDisabled = idx > 0 && selections[idx - 1] === null;

        return (
          <FormControl
            key={level.name}
            sx={{ ...valueInputSx, minWidth: 150 }}
            size="small"
            disabled={isDisabled}
          >
            <InputLabel>{level.label}</InputLabel>
            <Select
              value={selections[idx] || ''}
              label={level.label}
              onChange={(e) => handleLevelChange(idx, e.target.value)}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      })}
    </Box>
  );
}

function initializeSelectionsFromValue(
  value: FilterValue,
  levels: any[],
  setSelections: (selections: (string | number | null)[]) => void
) {
  if (typeof value === 'string') {
    try {
      const cascadeValues = JSON.parse(value) as CascadeValue[];
      const newSelections = levels.map((_, idx) => {
        const found = cascadeValues.find((v) => v.level === idx);
        return found?.value || null;
      });
      setSelections(newSelections);
    } catch (error) {
      console.error('Failed to parse cascade value:', error);
    }
  } else if (Array.isArray(value) && value.length > 0) {
    const cascadeValues = value as CascadeValue[];
    const newSelections = levels.map((_, idx) => {
      const found = cascadeValues.find((v) => v.level === idx);
      return found?.value || null;
    });
    setSelections(newSelections);
  }
}

async function loadOptionsForLevel(
  level: number,
  parentValue: string | number | undefined,
  cascadeConfig: any,
  setOptionsPerLevel: (fn: (prev: FieldOption[][]) => FieldOption[][]) => void
) {
  if (!cascadeConfig.loadOptions) return;

  try {
    const options = await cascadeConfig.loadOptions(level, parentValue);
    const mutableOptions = Array.from(options);

    setOptionsPerLevel((prev) => {
      const newOptions = [...prev];
      newOptions[level] = mutableOptions as FieldOption[];

      for (let i = level + 1; i < cascadeConfig.levels.length; i++) {
        newOptions[i] = [];
      }
      return newOptions;
    });
  } catch (error) {
    console.error('Failed to load cascade options', error);
  }
}

function handleLevelChangeImpl(
  level: number,
  selectedValue: string | number,
  selections: (string | number | null)[],
  setSelections: (selections: (string | number | null)[]) => void,
  cascadeConfig: any,
  optionsPerLevel: FieldOption[][],
  setOptionsPerLevel: (fn: (prev: FieldOption[][]) => FieldOption[][]) => void,
  onChange: (value: FilterValue) => void
) {
  const newSelections = [...selections];
  newSelections[level] = selectedValue;

  for (let i = level + 1; i < cascadeConfig.levels.length; i++) newSelections[i] = null;

  setSelections(newSelections);

  if (level < cascadeConfig.levels.length - 1)
    loadOptionsForLevel(level + 1, selectedValue, cascadeConfig, setOptionsPerLevel);

  const cascadeValues: CascadeValue[] = newSelections
    .map((val, idx) => {
      if (val === null) return null;
      const option = optionsPerLevel[idx]?.find((opt) => opt.value === val);
      return {
        level: idx,
        value: val,
        label: option?.label || String(val),
      };
    })
    .filter((v) => v !== null) as CascadeValue[];

  onChange(JSON.stringify(cascadeValues));
}
